import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';

import { type FileExtensionName, fileExtensionStylingMap, genericFile } from '~/components/FileExtensionPreview/types';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';

const isFileExtensionName = (ext: string): ext is FileExtensionName => ext in fileExtensionStylingMap;

export type UseAssetTitleBreadcrumbItemOptions = {
  assetTitleContent: ReactNode;
  assetSuffixContent?: ReactNode;
};

/**
 * The trail's final crumb — the asset itself, with its file-extension icon. Every asset-modal
 * breadcrumbs component appends this after whatever location it has (the standalone clip link has
 * none and renders it alone), so the shape stays single-sourced.
 */
export const useAssetTitleBreadcrumbItem = ({
  assetTitleContent,
  assetSuffixContent,
}: UseAssetTitleBreadcrumbItemOptions) => {
  const clip = useSelector(centralizedClipSelector);

  const { iconComponent: IconComponent } = useMemo(() => {
    const normalizedExt = clip?.ext?.toLowerCase() || '';

    return isFileExtensionName(normalizedExt) ? fileExtensionStylingMap[normalizedExt] : genericFile;
  }, [clip]);

  // Undefined until the centralized clip is seeded — callers render no title crumb yet.
  const assetTitleBreadcrumbItem = useMemo<BreadcrumbsItemType | undefined>(
    () =>
      clip
        ? {
            className: 'text-inherit shrink-[inherit] group/copy-path',
            id: clip.id,
            prefix: <IconComponent className="size-4 text-current" />,
            label: <span data-testid="ASSET_TITLE">{assetTitleContent}</span>,
            suffix: assetSuffixContent,
            type: 'item',
          }
        : undefined,
    [IconComponent, assetSuffixContent, assetTitleContent, clip],
  );

  return { assetTitleBreadcrumbItem };
};
